import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, KpiCard, SectionCard, ChartEmpty } from "@/components/kit";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { CHART } from "@/lib/helpers";
import { Crown, Users, ClipboardList, CheckCircle2, Activity, Target, CalendarCheck } from "lucide-react";
import { FaceEnrolmentNudge } from "@/components/FaceEnrolmentNudge";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend,
} from "recharts";

export default function ManagementOverview() {
  const { data, isLoading } = useQuery({
    queryKey: ["management"],
    queryFn: async () => (await api.get("/management/overview")).data,
    refetchInterval: 30000,
  });

  if (isLoading)
    return (
      <div>
        <PageHeader title="Management Overview" subtitle="Founder-level organisation health" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );

  const { kpi, charts, ranking, org_health } = data;
  const hasRanking = ranking?.some((r) => r.overall > 0);
  const hasTrend = charts.trend?.some((x) => x.present > 0 || x.accuracy > 0);

  return (
    <div data-testid="management-overview">
      <FaceEnrolmentNudge />
      <PageHeader title="Management Overview" subtitle="Founder's org-wide health & performance snapshot" />

      <div className="rounded-lg bg-gold text-navy-900 p-6 mb-6 flex items-center gap-4">
        <div className="rounded-lg bg-navy/15 p-3"><Crown className="h-7 w-7 text-navy-900" /></div>
        <div>
          <p className="text-navy-900 text-sm font-medium">Organisation Health Index</p>
          <p className="font-heading text-3xl font-extrabold tracking-tight">{org_health.avg_overall}%</p>
          <p className="text-navy/70 text-xs">Weighted across completion, productivity, attendance & accuracy</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard testId="mo-employees" label="Total Employees" value={kpi.total_employees} icon={Users} />
        <KpiCard testId="mo-tasks" label="Total Tasks" value={org_health.total_tasks} icon={ClipboardList} />
        <KpiCard testId="mo-completed" label="Completed" value={org_health.total_completed} icon={CheckCircle2} accent="green" />
        <KpiCard testId="mo-avg-attendance" label="Avg Attendance" value={`${org_health.avg_attendance}%`} icon={CalendarCheck} accent="gold" />
        <KpiCard testId="mo-avg-accuracy" label="Avg Accuracy" value={`${org_health.avg_accuracy}%`} icon={Target} accent="green" />
        <KpiCard testId="mo-productivity" label="Productivity" value={`${kpi.productivity_pct}%`} icon={Activity} accent="gold" />
        <KpiCard testId="mo-present" label="Present Today" value={kpi.present_today} icon={CheckCircle2} accent="green" />
        <KpiCard testId="mo-quality" label="Quality Score" value={kpi.quality_score} icon={Target} accent="gold" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <SectionCard title="Overall Rating by Employee" testId="mo-chart-rating">
          {hasRanking ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ranking} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip />
              <Bar dataKey="overall" name="Overall %" fill={CHART.navy} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
          ) : <ChartEmpty icon={Target} message="No performance ratings yet" height={300} />}
        </SectionCard>

        <SectionCard title="7-Day Attendance & Accuracy" testId="mo-chart-trend">
          {hasTrend ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={charts.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="present" name="Present" stroke={CHART.navy} strokeWidth={2} />
              <Line type="monotone" dataKey="accuracy" name="Accuracy %" stroke={CHART.gold} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
          ) : <ChartEmpty icon={CalendarCheck} message="No activity in the last 7 days" height={300} />}
        </SectionCard>
      </div>

      <SectionCard title="Team Leaderboard" testId="mo-leaderboard">
        {ranking?.length ? (
        <div className="space-y-3">
          {ranking.map((r) => (
            <div key={r.employee_id} className="flex items-center gap-4">
              <div className="w-8 text-center font-heading font-bold text-navy">{r.rank}</div>
              <div className="w-40 truncate text-sm font-medium text-slate-700">{r.name}</div>
              <Progress value={r.overall} className="flex-1 h-2.5" />
              <div className="w-14 text-right text-sm font-semibold text-navy">{r.overall}%</div>
            </div>
          ))}
        </div>
        ) : <ChartEmpty icon={Users} message="No employees to rank yet" height={160} />}
      </SectionCard>
    </div>
  );
}
